import { useEffect, useRef, useState } from 'react';

import { getCachedImage, putCachedImage } from '@src/lib/imageCache';

// After this age a cached image is still shown immediately, but refreshed in the
// background so a map replaced at the same path self-heals on the next view.
const STALE_AFTER = 1000 * 60 * 60 * 12; // 12 hours

// Shared across all hook instances so two consumers of the same image fetch once.
const inFlight = new Map<string, Promise<Blob | null>>();

// Stable cache key for a possibly-presigned URL. S3 presigned URLs carry a
// Signature/Expires query string that changes on every request, so the full URL
// is useless as a key — strip the query and key on the object path instead.
const cacheKeyFor = (url: string): string => {
  try {
    const u = new URL(url);

    return u.origin + u.pathname;
  } catch {
    return url.split('?')[0];
  }
};

const fetchAndStore = (fetchUrl: string, cacheKey: string): Promise<Blob | null> => {
  const existing = inFlight.get(cacheKey);

  if (existing) return existing;

  const request = (async () => {
    try {
      const res = await fetch(fetchUrl);

      if (!res.ok) return null;

      // Skip caching a 200 that is really an HTML error/login page or a PDF, but
      // still cache legacy uploads served as application/octet-stream (older maps
      // stored before the backend set a proper image content-type).
      const contentType = (res.headers.get('content-type') || '').toLowerCase();

      if (contentType.includes('text/html') || contentType.includes('application/pdf')) {
        return null;
      }

      const blob = await res.blob();

      void putCachedImage(cacheKey, blob);

      return blob;
    } catch {
      return null;
    } finally {
      inFlight.delete(cacheKey);
    }
  })();

  inFlight.set(cacheKey, request);

  return request;
};

/**
 * Resolves a remote image URL to a local object URL, backed by an IndexedDB
 * cache on the user's machine. First view fetches over the network and stores;
 * later views (including full page reloads) are served from local cache, so the
 * image no longer re-downloads each time.
 *
 * The cache is keyed by the object path, not the full URL, so a presigned S3 URL
 * (new signature every request) still resolves to a single cached entry. Falls
 * back to a plain remote load if the image can't be fetched (e.g. missing CORS).
 */
const useCachedImage = (url?: string | null): string | undefined => {
  const [src, setSrc] = useState<string | undefined>(undefined);

  // Latest (freshest-signature) URL to fetch with, without retriggering the
  // effect — the effect keys on the stable path so a new signature alone is a no-op.
  const urlRef = useRef<string | null | undefined>(url);

  urlRef.current = url;

  const cacheKey = url ? cacheKeyFor(url) : '';

  useEffect(() => {
    if (!cacheKey) {
      setSrc(undefined);

      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;

    const show = (blob: Blob) => {
      if (cancelled) return;

      if (objectUrl) URL.revokeObjectURL(objectUrl);

      objectUrl = URL.createObjectURL(blob);
      setSrc(objectUrl);
    };

    const resolve = async () => {
      const cached = await getCachedImage(cacheKey);

      if (cancelled) return;

      if (cached) {
        show(cached.blob);

        // Serve instantly from cache, but quietly refresh if it may be outdated.
        if (Date.now() - cached.ts > STALE_AFTER && urlRef.current) {
          void fetchAndStore(urlRef.current, cacheKey).then(fresh => fresh && show(fresh));
        }

        return;
      }

      const fetchUrl = urlRef.current;

      if (!fetchUrl) return;

      const fresh = await fetchAndStore(fetchUrl, cacheKey);

      if (cancelled) return;

      if (fresh) show(fresh);
      else setSrc(fetchUrl); // fall back to a plain browser load
    };

    void resolve();

    return () => {
      cancelled = true;

      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [cacheKey]);

  return src;
};

export default useCachedImage;
